import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { UnderlinedTabs, UnderlinedTabsContent, UnderlinedTabsList, UnderlinedTabsTrigger } from "@/components/ui/underlined-tabs";
import { useToast } from "@/hooks/use-toast";
import { associationApi, pricingTableApi, documentGroupApi } from "@/services/api";
import { financialAccountApi } from "@/services/api/financialAccountApi";
import { Association } from "@/models/Association";
import { DocumentGroup } from "@/models/DocumentGroup";
import { PricingTable } from "@/models/PricingTable";
import { useAppSettings } from "@/hooks/useAppSettings";
import { AssociationFormValues } from "@/types/association";
import { GeneralInformationTab } from "@/components/associations/edit-tabs/GeneralInformationTab";
import { BillingTab } from "@/components/associations/edit-tabs/BillingTab";
import { DefaultPropertyAddressTab } from "@/components/associations/edit-tabs/DefaultPropertyAddressTab";
import { ProjectDetailsTab } from "@/components/associations/edit-tabs/ProjectDetailsTab";
import { AssociationPropertiesTab } from "@/components/associations/AssociationPropertiesTab";
import { DeleteAssociationDialog } from "@/components/associations/DeleteAssociationDialog";
import { useAuth } from "@/contexts/AuthContext";

const AssociationEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<string>(
    location.state?.activeTab || 'general'
  );

  const { settings: appSettings } = useAppSettings();
  const { currentUser, selectedRole, hasPermission, getCurrentRoleIds, permissionsLoaded } = useAuth();

  // Check if user has write permission for associations in current role
  const canWriteAssociations = useMemo(() => {
    if (!selectedRole || !permissionsLoaded) return false;

    if (selectedRole.type === 'platform') {
      return hasPermission(getCurrentRoleIds(), 'write', 'associations');
    } else if (selectedRole.type === 'management_company') {
      return hasPermission(getCurrentRoleIds(), 'write', 'associations');
    }
    return false;
  }, [selectedRole, permissionsLoaded, hasPermission, getCurrentRoleIds]);

  // Redirect if user doesn't have write permission
  // Wait for both permissions and selectedRole to load before checking
  useEffect(() => {
    if (permissionsLoaded && selectedRole && !canWriteAssociations) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to edit associations.",
        variant: "destructive"
      });
      navigate('/backoffice/associations');
    }
  }, [permissionsLoaded, selectedRole, canWriteAssociations, navigate, toast]);

  // Helper function to get the current management company ID based on selected role
  const getCurrentManagementCompanyId = () => {
    if (!selectedRole) return currentUser?.management_company_id;
    
    // For platform roles, don't filter by management company (show all)
    if (selectedRole.type === 'platform') {
      return undefined;
    }
    
    // For management company roles, use the selected entity ID
    if (selectedRole.type === 'management_company') {
      return selectedRole.entityId;
    }
    
    // For association roles, use the current user's management company
    if (selectedRole.type === 'association') {
      return currentUser?.management_company_id;
    }
    
    return currentUser?.management_company_id;
  };

  const currentManagementCompanyId = getCurrentManagementCompanyId();

  const {
    data: association,
    isLoading: isAssociationLoading,
    error: associationError
  } = useQuery({
    queryKey: ["association", id],
    queryFn: () => associationApi.getById(Number(id)),
    enabled: !!id,
    meta: {
      onError: (err: Error) => {
        toast({
          title: "Error loading association",
          description: err.message,
          variant: "destructive"
        });
      }
    }
  });

  // Fetch financial accounts for this association
  const { data: financialAccounts = [] } = useQuery({
    queryKey: ['financialAccounts', id],
    queryFn: () => financialAccountApi.getAccounts({
      account_type: 'association',
      include_balances: true
    }),
    enabled: !!id,
  });

  const associationFinancialAccount = financialAccounts.find(
    (account: any) => account.reference === id || account.reference === Number(id)
  );
  const payoutAccount = associationFinancialAccount?.payout_accounts?.[0];

  console.log('Association ID:', id);
  console.log('Financial Accounts:', financialAccounts);
  console.log('Found Association Financial Account:', associationFinancialAccount);
  
  // Query for pricing tables with role-based filtering
  const { 
    data: pricingTables = [],
    isLoading: isPricingTablesLoading,
    error: pricingTableError
  } = useQuery({
    queryKey: ['pricing-tables', { management_company_id: currentManagementCompanyId }],
    queryFn: () => {
      if (selectedRole?.type === 'platform') {
        // Platform users see all pricing tables
        return pricingTableApi.list({ show_all: true });
      } else if (currentManagementCompanyId) {
        // Management company users see only their pricing tables
        return pricingTableApi.list({ 
          management_company_id: currentManagementCompanyId 
        });
      } else {
        throw new Error('Management company ID is required');
      }
    },
    enabled: !!selectedRole && (selectedRole.type === 'platform' || !!currentManagementCompanyId)
  });

  // Query for document groups with role-based filtering
  const { 
    data: documentGroups = [],
    isLoading: isDocumentGroupsLoading,
    error: documentGroupsError
  } = useQuery({
    queryKey: ['document-groups', { management_company_id: currentManagementCompanyId }],
    queryFn: () => {
      if (selectedRole?.type === 'platform') {
        // Platform users see all document groups
        return documentGroupApi.listWithFilters({});
      } else if (currentManagementCompanyId) {
        // Management company users see only their document groups
        return documentGroupApi.listWithFilters({ 
          management_company_id: currentManagementCompanyId 
        });
      } else {
        return [];
      }
    },
    enabled: !!selectedRole && (selectedRole.type === 'platform' || !!currentManagementCompanyId)
  });

  // Query for parent associations with role-based filtering
  const { 
    data: parentAssociations = [],
    isLoading: isParentAssociationsLoading,
    error: parentAssociationsError
  } = useQuery({
    queryKey: ['parent-associations', { management_company_id: currentManagementCompanyId }],
    queryFn: async () => {
      if (selectedRole?.type === 'platform') {
        // Platform users see all associations as potential parents
        const response = await associationApi.list({ queryKey: ['associations', {}] });
        return Array.isArray(response) ? response : response.data || [];
      } else if (currentManagementCompanyId) {
        // Management company users see only associations from their company
        const response = await associationApi.list({ 
          queryKey: ['associations', { management_company_id: currentManagementCompanyId }] 
        });
        return Array.isArray(response) ? response : response.data || [];
      } else {
        return [];
      }
    },
    enabled: !!selectedRole && (selectedRole.type === 'platform' || !!currentManagementCompanyId)
  });

  const form = useForm<AssociationFormValues>({
    defaultValues: {
      management_company_id: 0,
      name: "",
      association_type: "Condominium",
      accepts_external_applicants: false,
      email: "",
      phone: "",
      ein: "",
      tax_classification: null,
      default_property_address: "",
      default_property_city: "",
      default_property_state: "",
      default_property_zip: "",
      default_property_latitude: null,
      default_property_longitude: null,
      account_holder_name: "",
      bank_name: "",
      account_number: "",
      routing_number: "",
      account_type: "",
      street_address: "",
      payout_city: "",
      state_province: "",
      postcode: "",
      parent_association_id: null,
      pricing_table_id: appSettings?.defaultPricingTableId ? parseInt(appSettings.defaultPricingTableId) : null,
      document_groups: appSettings?.defaultDocumentGroups || [],
    }
  });

  const handleSelectDocumentGroup = (documentGroup: DocumentGroup) => {
    const currentGroups = form.getValues("document_groups") || [];
    if (!currentGroups.includes(documentGroup.id)) {
      form.setValue("document_groups", [...currentGroups, documentGroup.id]);
    }
  };

  const handleRemoveDocumentGroup = (groupId: number) => {
    const { data: documentGroups } = queryClient.getQueryData<{ data: DocumentGroup[] }>(["document-groups", { management_company_id: association?.management_company_id }]) || { data: [] };
    
    const groupToRemove = documentGroups?.find(g => g.id === groupId);
    
    if (groupToRemove && groupToRemove.association_id === Number(id)) {
      toast({
        title: "Cannot remove document group",
        description: "This document group is directly linked to this association and cannot be removed.",
        variant: "destructive"
      });
      return;
    }
    
    const currentGroups = form.getValues("document_groups") || [];
    form.setValue(
      "document_groups",
      currentGroups.filter((id) => id !== groupId)
    );
  };

  const handleBillingAddressSelect = (address: {
    fullAddress: string;
    city?: string;
    state?: string;
    zipCode?: string;
  }) => {
    form.setValue("street_address", address.fullAddress);
    if (address.city) form.setValue("payout_city", address.city);
    if (address.state) form.setValue("state_province", address.state);
    if (address.zipCode) form.setValue("postcode", address.zipCode);
  };

  const handleDefaultPropertyAddressSelect = (address: { 
    fullAddress: string; 
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lon?: number;
  }) => {
    form.setValue("default_property_address", address.fullAddress);
    
    if (address.city) form.setValue("default_property_city", address.city);
    if (address.state) form.setValue("default_property_state", address.state);
    if (address.zipCode) form.setValue("default_property_zip", address.zipCode);
    if (address.lat) form.setValue("default_property_latitude", address.lat);
    if (address.lon) form.setValue("default_property_longitude", address.lon);
  };

  useEffect(() => {
    if (association) {
      form.reset({
        management_company_id: association.management_company_id,
        name: association.name,
        association_type: association.association_type,
        email: association.email || "",
        phone: association.phone || "",
        ein: association.ein || "",
        tax_classification: association.tax_classification,
        default_property_address: association.default_property_address || "",
        default_property_city: association.default_property_city || "",
        default_property_state: association.default_property_state || "",
        default_property_zip: association.default_property_zip || "",
        default_property_latitude: association.default_property_latitude,
        default_property_longitude: association.default_property_longitude,
        // Load billing & payout data from payout_accounts only
        account_holder_name: payoutAccount?.account_holder_name || association.name || "",
        bank_name: payoutAccount?.bank_name || "",
        account_number: payoutAccount?.bank_account_number || "",
        routing_number: payoutAccount?.bank_routing_number || "",
        account_type: payoutAccount?.bank_account_type || "",
        street_address: payoutAccount?.street_address || "",
        payout_city: payoutAccount?.city || "",
        state_province: payoutAccount?.state_province || "",
        postcode: payoutAccount?.postcode || "",
        parent_association_id: association.parent_association_id,
        pricing_table_id: association.pricing_table_id,
        document_groups: association.document_groups || [],
        external_id: association.external_id,
        accepts_external_applicants:
          association.accepts_external_applicants === true,
      });
    }
  }, [association, payoutAccount, form]);

  useEffect(() => {
    if (location.state?.showDeleteDialog) {
      setDeleteDialogOpen(true);
    }
    
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);
  
  const updateMutation = useMutation({
    mutationFn: async (data: AssociationFormValues) => {
      // Filter out payout account fields before sending to association API
      const {
        account_holder_name,
        bank_name,
        account_number,
        routing_number,
        account_type,
        street_address,
        payout_city,
        state_province,
        postcode,
        ...associationData
      } = data;

      // Update the association first
      const updatedAssociation = await associationApi.update(Number(id), associationData);

      // Save payout account information if any financial fields are provided
      const hasFinancialData = account_holder_name || bank_name ||
                               account_number || routing_number;

      if (hasFinancialData) {
        console.log('Has financial data, attempting to save...');
        console.log('Association Financial Account:', associationFinancialAccount);

        if (!associationFinancialAccount) {
          // Financial account doesn't exist yet, try to fetch it again
          console.log('Financial account not found, fetching again...');
          try {
            const accounts = await financialAccountApi.getAccounts({
              account_type: 'association',
              include_balances: true
            });

            console.log('Fetched accounts:', accounts);
            const foundAccount = accounts.find(
              (account: any) => account.reference === id || account.reference === Number(id)
            );

            if (foundAccount) {
              console.log('Found financial account:', foundAccount);
              const payoutData = {
                payout_method: 'bank_account' as const,
                account_holder_name: account_holder_name || updatedAssociation.name,
                bank_name: bank_name || '',
                bank_account_number: account_number || '',
                bank_routing_number: routing_number || '',
                bank_account_type: account_type || 'checking',
                street_address: street_address || '',
                city: payout_city || '',
                state_province: state_province || '',
                postcode: postcode || '',
                is_default: true,
              };

              await financialAccountApi.createPayoutAccount(foundAccount.id, payoutData);
              console.log('Payout account saved successfully');
            } else {
              console.error('Financial account still not found after refetch');
              toast({
                variant: 'destructive',
                title: 'Error',
                description: 'Could not find financial account for this association. Please contact support.',
              });
            }
          } catch (payoutError) {
            console.error('Error saving payout account:', payoutError);
            toast({
              variant: 'destructive',
              title: 'Warning',
              description: 'Association updated but financial information could not be saved',
            });
          }
        } else {
          // Financial account exists, save directly
          try {
            const payoutData = {
              payout_method: 'bank_account' as const,
              account_holder_name: account_holder_name || updatedAssociation.name,
              bank_name: bank_name || '',
              bank_account_number: account_number || '',
              bank_routing_number: routing_number || '',
              bank_account_type: account_type || 'checking',
              street_address: street_address || '',
              city: payout_city || '',
              state_province: state_province || '',
              postcode: postcode || '',
              is_default: true,
            };

            console.log('Saving payout data:', payoutData);
            await financialAccountApi.createPayoutAccount(associationFinancialAccount.id, payoutData);
            console.log('Payout account saved successfully');
          } catch (payoutError) {
            console.error('Error saving payout account:', payoutError);
            toast({
              variant: 'destructive',
              title: 'Warning',
              description: 'Association updated but financial information could not be saved',
            });
          }
        }
      }

      return updatedAssociation;
    },
    onSuccess: () => {
      toast({
        title: "Association updated",
        description: "The association has been updated successfully."
      });
      queryClient.invalidateQueries({ queryKey: ["association", id] });
      queryClient.invalidateQueries({ queryKey: ["associations"] });
      queryClient.invalidateQueries({ queryKey: ["financialAccounts", id] });
      navigate("/backoffice/associations");
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating association",
        description: error.message,
        variant: "destructive"
      });
    }
  });
  
  const deleteMutation = useMutation({
    mutationFn: () => associationApi.delete(Number(id)),
    onSuccess: () => {
      toast({
        title: "Association deleted",
        description: "The association has been deleted successfully."
      });
      queryClient.invalidateQueries({ queryKey: ["associations"] });
      navigate("/backoffice/associations");
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting association",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const onSubmit = (data: AssociationFormValues) => {
    console.log("Submitting form with data:", data);
    updateMutation.mutate(data);
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    setDeleteDialogOpen(false);
  };

  const handleLocationSelect = (latitude: number, longitude: number) => {
    form.setValue("default_property_latitude", latitude);
    form.setValue("default_property_longitude", longitude);
  };

  const getPricingTableName = (id: number | null) => {
    if (!id) return "None (Use Default)";
    const table = pricingTables.find(t => t.id === id);
    return table ? table.name : "Unknown";
  };

  const isLoading = isAssociationLoading || isPricingTablesLoading || isDocumentGroupsLoading || isParentAssociationsLoading;

  // Show loading while permissions are being loaded
  if (!permissionsLoaded) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-full">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading permissions...</span>
        </div>
      </BackofficeLayout>
    );
  }

  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-full">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </BackofficeLayout>
    );
  }

  if (associationError || pricingTableError || documentGroupsError || parentAssociationsError || !association) {
    return (
      <BackofficeLayout>
        <div className="text-center">
          <h2 className="text-2xl font-bold">Error loading association</h2>
          <p className="text-gray-500">
            {associationError ? `${associationError}` : 
             pricingTableError ? `${pricingTableError}` : 
             documentGroupsError ? `${documentGroupsError}` :
             parentAssociationsError ? `${parentAssociationsError}` : 
             "Association not found"}
          </p>
          <Button 
            className="mt-4" 
            onClick={() => navigate("/backoffice/associations")}
          >
            Back to Associations
          </Button>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">
            {association ? association.name : "Edit Association"}
          </h1>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate(`/backoffice/associations`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Associations
            </Button>
          </div>
        </div>
        
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <UnderlinedTabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <UnderlinedTabsList>
                <UnderlinedTabsTrigger value="general">General Information</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="billing">Billing</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="location">Default Property Address</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="properties">Properties</UnderlinedTabsTrigger>
                <UnderlinedTabsTrigger value="project">Project Details</UnderlinedTabsTrigger>
              </UnderlinedTabsList>
              
              <UnderlinedTabsContent value="general" className="space-y-4">
                <GeneralInformationTab 
                  pricingTables={pricingTables}
                  onSelectDocumentGroup={handleSelectDocumentGroup}
                  onRemoveDocumentGroup={handleRemoveDocumentGroup}
                  getPricingTableName={getPricingTableName}
                  isEditing={true} 
                  associationId={Number(id)}
                  managementCompanyId={currentManagementCompanyId}
                />
              </UnderlinedTabsContent>
              
              <UnderlinedTabsContent value="billing" className="space-y-4">
                <BillingTab handleBillingAddressSelect={handleBillingAddressSelect} />
              </UnderlinedTabsContent>
              
              <UnderlinedTabsContent value="location" className="space-y-4">
                <DefaultPropertyAddressTab 
                  handleDefaultPropertyAddressSelect={handleDefaultPropertyAddressSelect}
                  handleLocationSelect={handleLocationSelect}
                />
              </UnderlinedTabsContent>
              
              <UnderlinedTabsContent value="properties" className="space-y-4">
                {association && (
                  <AssociationPropertiesTab association={association} />
                )}
              </UnderlinedTabsContent>

              <UnderlinedTabsContent value="project" className="space-y-4">
                {association && (
                  <ProjectDetailsTab associationId={Number(id)} associationName={association.name} />
                )}
              </UnderlinedTabsContent>
              
              {/* Project Details saves itself (its own table, its own button);
                  showing the association's Save here too made the nearest
                  button at the bottom of that tab save the WRONG thing and
                  navigate away — Mateo hit exactly that on 3-Sep. */}
              {activeTab !== "project" && (
                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/backoffice/associations`)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Save Changes
                  </Button>
                </div>
              )}
            </UnderlinedTabs>
          </form>
        </FormProvider>
        
        <DeleteAssociationDialog 
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDelete={handleDelete}
          isPending={deleteMutation.isPending}
        />
      </div>
    </BackofficeLayout>
  );
};

export default AssociationEdit;
